import React, { useEffect, useState } from 'react';
import { FaBed, FaBath, FaRulerCombined, FaStar, FaHeart, FaShare, FaMapMarkerAlt } from 'react-icons/fa';
import PropertyBookingModal from '../book/PropertyBookingModal';
import { getSingleProperty } from '../../api/user/property/property';
import { useParams } from 'react-router-dom';
import Loader from '../common/Loader';
import RentRoomList from '../home/listings/rent/RentRoomList';
import RentBookingForm from '../home/listings/rent/RentBookingForm';
import BuyBookingForm from '../home/listings/buy/BuyBookingForm';
import SEO from '../seo/SEO';
import styles from './css/PropertyDetails.module.css';
import UserProtectedRoute from '../protected-routes/user-protected-route';

const PropertyDetails = ({ onClose }) => {
  const { id } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bookProperty, isBookProperty] = useState(false);
  const [property, setProperty] = useState({ data: {}, files: [], userDetails: {}, rooms: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isVerifyingSession, setIsVerifyingSession] = useState(false);
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);

  const isLand = property?.data?.type === 'land' || property?.category?.name?.toLowerCase().includes('land');

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setIsLoading(true);
        const response = await getSingleProperty(id);
        setProperty(response.property || { data: {}, files: [], userDetails: {}, rooms: [] });
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  const showBookModal = () => {
    isBookProperty(!bookProperty);
  };

  const nextImage = () => {
    if (!property.files?.length) return;
    setCurrentImageIndex((prevIndex) =>
      prevIndex === property.files.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    if (!property.files?.length) return;
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? property.files.length - 1 : prevIndex - 1
    );
  };

  const openFullscreen = () => {
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
    // Scroll to top when room is selected
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseBookingForm = () => {
    setSelectedRoom(null);
  };

  const handleOpenBuyModal = () => {
    setIsBuyModalOpen(true);
  };

  const handleCloseBuyModal = () => {
    setIsBuyModalOpen(false);
  };

  if (isLoading) return <Loader />;
  if (error) return <div className={styles.errorMessage}>{error}</div>;
  if (!property.data) return <div className={styles.notFound}>Property not found</div>;

  return (
    <>
      <SEO
        title={property.data.name || 'Property Details'}
        description={property.data.description || `View this property located in ${property.data.location || 'a great location'}. ${property.data.special_amenities?.join(', ') || 'Great amenities available.'}`}
        keywords={`property, ${property.category?.name || ''}, ${property.data.location || ''}, rental, accommodation, ${property.data.special_amenities?.join(', ') || ''}`}
        ogImage={property.files?.[0]?.url}
        ogType="property"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'RealEstateAgent',
          name: property.data.name || 'Property',
          description: property.data.description,
          address: {
            '@type': 'PostalAddress',
            streetAddress: property.data.address || '',
            addressLocality: property.data.location || '',
            addressCountry: 'CM'
          },
          priceRange: property.rooms?.length > 0 ? `$${Math.min(...property.rooms.map(r => r.price))} - $${Math.max(...property.rooms.map(r => r.price))}` : undefined,
          amenityFeature: property.data.special_amenities?.map(amenity => ({
            '@type': 'LocationFeatureSpecification',
            name: amenity
          })) || []
        }}
      />
      {bookProperty && <PropertyBookingModal showBookModal={showBookModal} />}

      <div className={styles.propertyDetailsContainer}>
        {onClose && (
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        )}

        {/* Property Header */}
        <div className={styles.propertyHeader}>
          <div className={styles.headerContent}>
            <h1 className={styles.propertyTitle}>{property.data.name || 'Property Name'}</h1>
            <div className={styles.propertyLocation}>
              <FaMapMarkerAlt className={styles.locationIcon} />
              <span>{property.data.location || 'Location not specified'}</span>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        <div className={styles.propertyGallery}>
          <div className={styles.galleryHeader}>
            <h2 className={styles.galleryTitle}>Property Photos</h2>
            <span className={styles.imageCount}>{property.files?.length || 0} photos</span>
          </div>

          {property.files?.length > 0 ? (
            <div className={styles.galleryContainer}>
              <div className={styles.mainImageContainer}>
                <img
                  src={`${property.files[currentImageIndex]?.url || ''}`}
                  alt={`Property view ${currentImageIndex + 1}`}
                  className={styles.mainImage}
                  onError={(e) => {
                    e.target.src = '/placeholder-property.jpg';
                  }}
                />
                <button
                  className={styles.fullscreenButton}
                  onClick={openFullscreen}
                  aria-label="View full screen"
                >
                  🔍
                </button>

                {property.files.length > 1 && (
                  <>
                    <button
                      className={`${styles.galleryNav} ${styles.prevButton}`}
                      onClick={prevImage}
                      aria-label="Previous image"
                    >
                      ‹
                    </button>
                    <button
                      className={`${styles.galleryNav} ${styles.nextButton}`}
                      onClick={nextImage}
                      aria-label="Next image"
                    >
                      ›
                    </button>
                    <div className={styles.imageCounter}>
                      <span className={styles.currentImage}>{currentImageIndex + 1}</span>
                      <span className={styles.slideSeparator}>/</span>
                      <span className={styles.totalImages}>{property.files.length}</span>
                    </div>
                  </>
                )}
              </div>

              {property.files.length > 1 && (
                <div className={styles.thumbnailGrid}>
                  {property.files.slice(0, 4).map((img, index) => (
                    <button
                      key={index}
                      className={`${styles.thumbnail} ${index === currentImageIndex ? styles.active : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img
                        src={`${img.url}`}
                        alt={`Thumbnail ${index + 1}`}
                      />
                    </button>
                  ))}
                  {property.files.length > 4 && (
                    <button
                      className={styles.moreThumbnails}
                      onClick={() => setCurrentImageIndex(4)}
                    >
                      +{property.files.length - 4} more
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.noImages}>
              <img
                src="/placeholder-property.jpg"
                alt="No property images"
                className={styles.defaultImage}
              />
              <div className={styles.noImagesText}>No photos available</div>
            </div>
          )}
        </div>

        {/* Publisher Info */}
        {/* {property.userDetails && (
          <div className={styles.publisherSection}>
            <div className={styles.publisherCard}>
              <div className={styles.publisherAvatar}>
                <img 
                  src={property.userDetails.avatar} 
                  alt={property.userDetails.first_name} 
                  onError={(e) => {
                    e.target.src = '/default-avatar.jpg';
                  }}
                />
                {property.userDetails.verified && (
                  <span className={styles.verifiedBadge}>✓</span>
                )}
              </div>
              <div className={styles.publisherInfo}>
                <h3 className={styles.publisherName}>
                  {property.userDetails.first_name} {property.userDetails.last_name}
                </h3>
                <p className={styles.publisherJoined}>
                  {property.userDetails.joined || 'Member'}
                </p>
              </div>
              <button className={styles.contactButton}>
                Contact
              </button>
            </div>
          </div>
        )} */}

        {/* Property Details */}
        {!isLand && (
          <div className={styles.propertyDetailsSection}>
            <h2 className={styles.sectionTitle}>Property Details</h2>
            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <FaBed />
                </div>
                <div className={styles.detailContent}>
                  <span className={styles.detailValue}>{property.data?.floors || 'N/A'}</span>
                  <span className={styles.detailLabel}>Floors</span>
                </div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <FaBath />
                </div>
                <div className={styles.detailContent}>
                  <span className={styles.detailValue}>{property.data?.rooms || 'N/A'}</span>
                  <span className={styles.detailLabel}>Rooms</span>
                </div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <FaRulerCombined />
                </div>
                <div className={styles.detailContent}>
                  <span className={styles.detailValue}>{property.area || 'N/A'}</span>
                  <span className={styles.detailLabel}>sq ft</span>
                </div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <FaStar />
                </div>
                <div className={styles.detailContent}>
                  <span className={styles.detailValue}>{property.rating || 'N/A'}</span>
                  <span className={styles.detailLabel}>Rating</span>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Property Content Grid */}
        <div className={`${styles.propertyContent} ${isLand ? styles.propertyContentLand : ''}`}>
          <div className={styles.propertyMain}>
            {/* Description */}
            {property.data.description && (
              <div className={styles.descriptionSection}>
                <h2 className={styles.sectionTitle}>Description</h2>
                <div className={styles.descriptionCard}>
                  <p className={styles.descriptionText}>{property.data.description}</p>
                </div>
              </div>
            )}

            {/* Special Amenities */}
            {property.data.special_amenities?.length > 0 && (
              <div className={styles.amenitiesSection}>
                <h2 className={styles.sectionTitle}>Special Amenities</h2>
                <div className={styles.amenitiesGrid}>
                  {property.data.special_amenities.map((amenity, index) => (
                    <div key={index} className={styles.amenityItem}>
                      <span className={styles.amenityIcon}>✓</span>
                      <span className={styles.amenityName}>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rooms Section */}
            {!isLand && (
              <div className={styles.roomsSection}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>Apartments & Rates</h2>
                  <span className={styles.roomCount}>
                    {property.rooms?.length || 0} apartment types
                  </span>
                </div>
                <RentRoomList
                  rooms={property.rooms || []}
                  onSelectRoom={handleSelectRoom}
                  isVerifyingSession={isVerifyingSession}
                  selectedRoomId={selectedRoom?._id}
                />
              </div>
            )}
          </div>

          {/* Sidebar - Only shown when no room is selected */}
          {!selectedRoom && (
            <div className={styles.propertySidebar}>
              {isLand ? (
                <div className={styles.bookingPlaceholder}>
                  <div className={styles.placeholderIcon}>🌍</div>
                  <h3>Buy This Property</h3>
                  <p>You are purchasing the entire plot. Click below to request purchase and negotiate with the owner.</p>
                  <button onClick={handleOpenBuyModal} className={styles.purchaseLandBtn}>
                    Purchase Property
                  </button>
                </div>
              ) : (
                <div className={styles.bookingPlaceholder}>
                  <div className={styles.placeholderIcon}>🏡</div>
                  <h3>Select an Apartment</h3>
                  <p>Choose your preferred apartment from the list bellow, check availability, and proceed with booking</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <button className={styles.likeButton}>
            <FaHeart className={styles.buttonIcon} />
            <span>Like</span>
          </button>
          <button className={styles.shareButton}>
            <FaShare className={styles.buttonIcon} />
            <span>Share</span>
          </button>
        </div>
      </div>

      {/* Booking Form Modal */}
      {selectedRoom && (
        <UserProtectedRoute onValidationChange={setIsVerifyingSession} hideLoader={true}>
          <div className={styles.bookingFormModal}>
            <div className={styles.bookingFormModalContent}>
              <RentBookingForm
                propertyId={property._id}
                room={selectedRoom}
                onClose={handleCloseBookingForm}
              />
            </div>
          </div>
        </UserProtectedRoute>
      )}

      {/* Buy Form Modal */}
      {isBuyModalOpen && (
        <UserProtectedRoute onValidationChange={setIsVerifyingSession} hideLoader={true}>
          <div className={styles.bookingFormModal}>
            <div className={styles.bookingFormModalContent}>
              <BuyBookingForm
                propertyId={property._id}
                property={property}
                onClose={handleCloseBuyModal}
              />
            </div>
          </div>
        </UserProtectedRoute>
      )}

      {/* Fullscreen Modal */}
      {isFullscreen && property.files?.length > 0 && (
        <div className={styles.fullscreenModal} onClick={closeFullscreen}>
          <div className={styles.fullscreenContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeFullscreen} onClick={closeFullscreen}>
              ✕
            </button>
            <img
              src={`${property.files[currentImageIndex].url}`}
              alt={`Property view - Fullscreen`}
              className={styles.fullscreenImage}
            />
            {property.files.length > 1 && (
              <>
                <button
                  className={`${styles.fullscreenNav} ${styles.fullscreenPrev}`}
                  onClick={prevImage}
                >
                  ‹
                </button>
                <button
                  className={`${styles.fullscreenNav} ${styles.fullscreenNext}`}
                  onClick={nextImage}
                >
                  ›
                </button>
                <div className={styles.fullscreenCounter}>
                  {currentImageIndex + 1} / {property.files.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PropertyDetails;